/**
 * Demo Event Bus
 * Centralized event management system for demo interactions and step advancement
 * Implements publish-subscribe pattern for decoupled event handling
 */

/**
 * Event Bus class for managing demo events
 * @class DemoEventBus
 */
class DemoEventBus {
  constructor() {
    /**
     * Map of event listeners
     * @type {Map<string, Set<Function>>}
     */
    this.listeners = new Map();

    /**
     * Event history for debugging and analytics
     * @type {Array<{event: string, data: any, timestamp: number}>}
     */
    this.history = [];

    /**
     * Max events to keep in history
     * @type {number}
     */
    this.maxHistorySize = 100;
  }

  /**
   * Subscribe to an event
   * @param {string} eventName - Name of the event to listen for
   * @param {Function} callback - Function to call when event is emitted
   * @returns {Function} - Unsubscribe function
   * @throws {Error} if eventName is not a string or callback is not a function
   */
  on(eventName, callback) {
    if (typeof eventName !== 'string') {
      throw new Error('[DemoEventBus] Event name must be a string');
    }
    if (typeof callback !== 'function') {
      throw new Error('[DemoEventBus] Callback must be a function');
    }

    // Create listener set if it doesn't exist
    if (!this.listeners.has(eventName)) {
      this.listeners.set(eventName, new Set());
    }

    this.listeners.get(eventName).add(callback);

    // Return unsubscribe function
    return () => this.off(eventName, callback);
  }

  /**
   * Subscribe to an event only once
   * @param {string} eventName - Name of the event to listen for
   * @param {Function} callback - Function to call when event is emitted
   * @returns {Function} - Unsubscribe function
   */
  once(eventName, callback) {
    if (typeof eventName !== 'string') {
      throw new Error('[DemoEventBus] Event name must be a string');
    }
    if (typeof callback !== 'function') {
      throw new Error('[DemoEventBus] Callback must be a function');
    }

    const wrappedCallback = (data) => {
      callback(data);
      this.off(eventName, wrappedCallback);
    };

    return this.on(eventName, wrappedCallback);
  }

  /**
   * Unsubscribe from an event
   * @param {string} eventName - Name of the event
   * @param {Function} callback - Callback function to remove
   * @returns {boolean} - True if listener was removed, false otherwise
   */
  off(eventName, callback) {
    if (!this.listeners.has(eventName)) {
      return false;
    }

    const removed = this.listeners.get(eventName).delete(callback);

    // Clean up empty listener sets
    if (this.listeners.get(eventName).size === 0) {
      this.listeners.delete(eventName);
    }

    return removed;
  }

  /**
   * Emit an event with optional data
   * @param {string} eventName - Name of the event to emit
   * @param {any} data - Optional data to pass to listeners
   * @returns {number} - Number of listeners called
   */
  emit(eventName, data = null) {
    if (typeof eventName !== 'string') {
      throw new Error('[DemoEventBus] Event name must be a string');
    }

    // Add to history
    this._addToHistory(eventName, data);

    // Call all listeners
    if (!this.listeners.has(eventName)) {
      return 0;
    }

    let count = 0;
    try {
      this.listeners.get(eventName).forEach((callback) => {
        try {
          callback(data);
          count++;
        } catch (error) {
          console.error(`[DemoEventBus] Error in listener for event "${eventName}":`, error);
        }
      });
    } catch (error) {
      console.error(`[DemoEventBus] Error emitting event "${eventName}":`, error);
    }

    return count;
  }

  /**
   * Get all listeners for an event
   * @param {string} eventName - Name of the event
   * @returns {Array<Function>} - Array of callback functions
   */
  listeners_for(eventName) {
    if (!this.listeners.has(eventName)) {
      return [];
    }
    return Array.from(this.listeners.get(eventName));
  }

  /**
   * Get count of listeners for an event
   * @param {string} eventName - Name of the event
   * @returns {number} - Number of listeners
   */
  listenerCount(eventName) {
    return this.listeners_for(eventName).length;
  }

  /**
   * Clear all listeners
   * @param {string|null} eventName - Optional event name to clear only that event
   */
  clear(eventName = null) {
    if (eventName) {
      this.listeners.delete(eventName);
    } else {
      this.listeners.clear();
    }
  }

  /**
   * Get event history
   * @returns {Array} - Event history array
   */
  getHistory() {
    return [...this.history];
  }

  /**
   * Clear event history
   */
  clearHistory() {
    this.history = [];
  }

  /**
   * Add event to history with size limit
   * @private
   * @param {string} eventName - Name of event
   * @param {any} data - Event data
   */
  _addToHistory(eventName, data) {
    this.history.push({
      event: eventName,
      data,
      timestamp: Date.now(),
    });

    // Remove oldest events if history exceeds max size
    if (this.history.length > this.maxHistorySize) {
      this.history = this.history.slice(-this.maxHistorySize);
    }
  }
}

/**
 * Global singleton instance of DemoEventBus
 * @type {DemoEventBus}
 */
let demoEventBusInstance = null;

/**
 * Get or create the global DemoEventBus instance
 * @returns {DemoEventBus} - Global event bus instance
 */
export function getDemoEventBus() {
  if (!demoEventBusInstance) {
    demoEventBusInstance = new DemoEventBus();
  }
  return demoEventBusInstance;
}

/**
 * Reset the event bus (mainly for testing)
 */
export function resetDemoEventBus() {
  if (demoEventBusInstance) {
    demoEventBusInstance.clear();
    demoEventBusInstance.clearHistory();
  }
  demoEventBusInstance = null;
}

export default DemoEventBus;
